import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, card, btnPrimary } from "./AdminShell";
import { Trash2, Copy, X, Image as ImageIcon, ExternalLink, Upload } from "lucide-react";

const formatBytes = (b) => {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

export function AdminMedia() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | unused
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const refresh = () => {
    setLoading(true);
    api.get("/admin/media", { params: filter === "unused" ? { unused_only: true } : {} })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, [filter]);

  const filtered = items.filter((i) => !search || (i.original_filename || "").toLowerCase().includes(search.toLowerCase()));

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    let ok = 0;
    for (const file of Array.from(files)) {
      try {
        const fd = new FormData(); fd.append("file", file);
        await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok++;
      } catch (e) { toast.error(e.response?.data?.detail || `Gagal upload ${file.name}`); }
    }
    if (ok) toast.success(`${ok} foto berhasil diunggah.`);
    setUploading(false); refresh();
  };

  const del = async (item, force = false) => {
    try {
      await api.delete(`/admin/media/${item.storage_path}`, { params: force ? { force: true } : {} });
      toast.success("File dihapus.");
      setPreview(null); refresh();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "object" && detail?.usages) {
        const names = detail.usages.map((u) => u.name).join(", ");
        if (window.confirm(`File masih dipakai oleh: ${names}\n\nTetap hapus?`)) del(item, true);
      } else {
        toast.error(typeof detail === "string" ? detail : "Gagal menghapus");
      }
    }
  };

  const copyUrl = (item) => {
    const full = `${process.env.REACT_APP_BACKEND_URL}${item.url}`;
    navigator.clipboard.writeText(full);
    toast.success("URL disalin.");
  };

  return (
    <div>
      <PageHeader title="Media Library" subtitle="Semua foto yang diunggah ke Emergent Object Storage"
        actions={
          <label className={`${btnPrimary} h-10 px-4 inline-flex items-center gap-2 cursor-pointer`} data-testid="media-upload">
            <Upload size={14} /> {uploading ? "Mengunggah..." : "Upload Foto"}
            <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload(e.target.files)} disabled={uploading} />
          </label>
        } />

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex gap-1 bg-neutral-100 p-1 rounded-md">
          {[["all", "Semua"], ["unused", "Tidak Terpakai"]].map(([v, l]) => (
            <button key={v} onClick={() => setFilter(v)}
              className={`px-3 py-1.5 text-xs font-semibold rounded ${filter === v ? "bg-white text-[#FF9418] shadow-sm" : "text-neutral-600"}`}
              data-testid={`media-filter-${v}`}>{l}</button>
          ))}
        </div>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari filename..."
          className="flex-1 min-w-[200px] max-w-md bg-white border border-neutral-300 rounded-md px-3 py-1.5 text-sm outline-none focus:border-[#FF9418]" />
      </div>

      {loading ? <div className="text-neutral-500">Memuat...</div> :
        filtered.length === 0 ? <div className={`${card} p-8 text-center text-neutral-500`}>Tidak ada file.</div> :
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {filtered.map((f) => (
              <div key={f.id} className={`${card} overflow-hidden group cursor-pointer`} onClick={() => setPreview(f)} data-testid={`media-item-${f.id}`}>
                <div className="aspect-square bg-neutral-100 relative overflow-hidden">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${f.url}`} alt="" className="w-full h-full object-cover group-hover:scale-105 transition" loading="lazy" />
                </div>
                <div className="p-2">
                  <div className="text-[11px] text-neutral-900 truncate font-medium" title={f.original_filename}>{f.original_filename || f.storage_path.split("/").pop()}</div>
                  <div className="text-[10px] text-neutral-500 flex items-center justify-between mt-0.5">
                    <span>{formatBytes(f.size)}</span>
                    {f.usages?.length > 0 && <span className="bg-green-50 text-green-700 px-1 rounded text-[9px] font-mono">{f.usages.length}×</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>}

      {preview && <MediaPreview item={preview} onClose={() => setPreview(null)} onDelete={() => del(preview)} onCopy={() => copyUrl(preview)} />}
    </div>
  );
}

function MediaPreview({ item, onClose, onDelete, onCopy }) {
  const url = `${process.env.REACT_APP_BACKEND_URL}${item.url}`;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-neutral-200 flex items-center justify-between">
          <div className="font-semibold text-neutral-900 truncate flex-1">{item.original_filename}</div>
          <button onClick={onClose} className="text-neutral-500 ml-2"><X size={18} /></button>
        </div>
        <div className="p-4">
          <img src={url} alt="" className="w-full max-h-[50vh] object-contain bg-neutral-100 rounded-md" />
          <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
            <div><div className="text-[11px] text-neutral-500 font-mono uppercase tracking-wider">Ukuran</div><div>{formatBytes(item.size)}</div></div>
            <div><div className="text-[11px] text-neutral-500 font-mono uppercase tracking-wider">Diunggah</div><div>{formatDate(item.created_at)}</div></div>
            <div className="col-span-2"><div className="text-[11px] text-neutral-500 font-mono uppercase tracking-wider">URL</div>
              <div className="text-xs font-mono bg-neutral-50 rounded px-2 py-1 break-all">{url}</div>
            </div>
          </div>
          <div className="mt-3">
            <div className="text-[11px] text-neutral-500 font-mono uppercase tracking-wider mb-1">Dipakai Oleh</div>
            {item.usages?.length === 0 ? <div className="text-sm text-neutral-500">Tidak ada.</div> :
              <div className="flex flex-wrap gap-2">
                {item.usages?.map((u, i) => (
                  <span key={i} className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded font-mono">{u.name || u.id}</span>
                ))}
              </div>}
          </div>
          <div className="flex gap-2 mt-5">
            <Button onClick={onCopy} variant="outline" className="border-neutral-300 h-10"><Copy size={14} /> Copy URL</Button>
            <a href={url} target="_blank" rel="noreferrer"><Button variant="outline" className="border-neutral-300 h-10"><ExternalLink size={14} /> Buka</Button></a>
            <Button onClick={onDelete} className="bg-red-600 hover:bg-red-700 text-white h-10 ml-auto"><Trash2 size={14} /> Hapus</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
